import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Card, CardContent, Button, IconButton, Alert, Chip,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, Select,
  MenuItem, FormControl, InputLabel, Switch, FormControlLabel,
  CircularProgress, Snackbar, Tooltip,
} from '@mui/material';
import {
  Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon,
  PlayArrow as PlayArrowIcon, Science as ScienceIcon, Rule as RuleIcon,
} from '@mui/icons-material';
import { MainLayout } from '../components/MainLayout';
import { EmptyState } from '../components/EmptyState';
import { RuleTestResultsDialog } from '../components/RuleTestResultsDialog';
import { CategoriesService, type CategoryWithGroup } from '../services/categories-service';
import {
  CategorizationRulesService,
  type CategorizationRule,
  type CreateCategorizationRuleRequest,
  type RuleTestResult,
} from '../services/categorization-rules-service';

/** Human-readable labels for match fields. */
const MATCH_FIELD_LABELS: Record<string, string> = {
  description: 'Description', amount: 'Amount', account: 'Account',
};

/** Human-readable labels for match types. */
const MATCH_TYPE_LABELS: Record<string, string> = {
  contains: 'contains', exact: 'equals', starts_with: 'starts with',
  ends_with: 'ends with', regex: 'matches regex', range: 'is between',
};

/**
 * Builds a human-readable summary of a rule's match condition.
 *
 * Args:
 *     rule (CategorizationRule): The rule to describe.
 *
 * Returns:
 *     string: e.g. "Description contains 'Netflix'"
 */
function describeCondition(rule: CategorizationRule): string {
  const field = MATCH_FIELD_LABELS[rule.match_field] || rule.match_field;
  const type = MATCH_TYPE_LABELS[rule.match_type] || rule.match_type;
  if (rule.match_type === 'range') {
    return `${field} ${type} ${rule.match_value} and ${rule.match_value_2 || '?'}`;
  }
  return `${field} ${type} '${rule.match_value}'`;
}

/** Default form state for creating/editing a rule. */
const DEFAULT_FORM: CreateCategorizationRuleRequest = {
  name: '', match_field: 'description', match_type: 'contains',
  match_value: '', match_value_2: undefined, category_id: '',
  priority: 0, is_active: true,
};

export const CategorizationRulesPage: React.FC = () => {
  const [rules, setRules] = useState<CategorizationRule[]>([]);
  const [categories, setCategories] = useState<CategoryWithGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState<string | null>(null);

  // Create/Edit dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRule, setEditingRule] = useState<CategorizationRule | null>(null);
  const [form, setForm] = useState<CreateCategorizationRuleRequest>({ ...DEFAULT_FORM });
  const [saving, setSaving] = useState(false);

  // Test results dialog state
  const [testDialogOpen, setTestDialogOpen] = useState(false);
  const [testResults, setTestResults] = useState<RuleTestResult[]>([]);
  const [testTotal, setTestTotal] = useState(0);
  const [testing, setTesting] = useState(false);
  const [applying, setApplying] = useState(false);

  /** Fetch rules and categories from the backend. */
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [rulesRes, categoriesRes] = await Promise.all([
        CategorizationRulesService.list(),
        CategoriesService.getCategories(),
      ]);
      setRules(rulesRes.categorization_rules);
      setCategories(categoriesRes.data.categories);
      setError(null);
    } catch (err: any) {
      setError(err.message || 'Failed to load categorization rules');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  /** Look up a category name by its ID. */
  const getCategoryName = (categoryId: string): string => {
    const cat = categories.find((c) => c.id === categoryId);
    return cat ? `${cat.group_name} > ${cat.name}` : 'Unknown';
  };

  const openNewDialog = () => {
    setEditingRule(null);
    setForm({ ...DEFAULT_FORM });
    setDialogOpen(true);
  };

  const openEditDialog = (rule: CategorizationRule) => {
    setEditingRule(rule);
    setForm({
      name: rule.name, match_field: rule.match_field, match_type: rule.match_type,
      match_value: rule.match_value, match_value_2: rule.match_value_2 || undefined,
      category_id: rule.category_id, priority: rule.priority, is_active: rule.is_active,
    });
    setDialogOpen(true);
  };

  const closeDialog = () => { setDialogOpen(false); setEditingRule(null); };

  const handleSave = async () => {
    try {
      setSaving(true);
      if (editingRule) {
        await CategorizationRulesService.update(editingRule.id, form);
        setSnackbar('Rule updated successfully');
      } else {
        await CategorizationRulesService.create(form);
        setSnackbar('Rule created successfully');
      }
      closeDialog();
      fetchData();
    } catch (err: any) {
      setError(err.message || 'Failed to save rule');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Delete this categorization rule?')) return;
    try {
      await CategorizationRulesService.delete(id);
      setSnackbar('Rule deleted');
      fetchData();
    } catch (err: any) {
      setError(err.message || 'Failed to delete rule');
    }
  };

  const handleToggleActive = async (rule: CategorizationRule) => {
    try {
      await CategorizationRulesService.update(rule.id, { is_active: !rule.is_active });
      fetchData();
    } catch (err: any) {
      setError(err.message || 'Failed to toggle rule');
    }
  };

  const handleApplyRules = async () => {
    try {
      setApplying(true);
      const result = await CategorizationRulesService.applyRules();
      setSnackbar(`Applied rules: ${result.transactions_updated} transaction(s) updated`);
      setError(null);
    } catch (err: any) {
      setError(err.message || 'Failed to apply rules');
    } finally {
      setApplying(false);
    }
  };

  const handleTestRules = async () => {
    try {
      setTesting(true);
      const result = await CategorizationRulesService.testRules();
      setTestResults(result.test_results);
      setTestTotal(result.total);
      setTestDialogOpen(true);
      setError(null);
    } catch (err: any) {
      setError(err.message || 'Failed to test rules');
    } finally {
      setTesting(false);
    }
  };

  const activeCount = rules.filter((r) => r.is_active).length;
  const isFormValid = form.name && form.match_value && form.category_id;

  if (loading) {
    return (
      <MainLayout>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <Box>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box>
            <Typography variant="h4" fontWeight="bold">Categorization Rules</Typography>
            <Typography variant="body2" color="text.secondary">
              Automatically categorize transactions based on matching rules
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button
              variant="outlined"
              startIcon={testing ? <CircularProgress size={18} /> : <ScienceIcon />}
              onClick={handleTestRules}
              disabled={testing || rules.length === 0}
            >
              Test Rules
            </Button>
            <Button
              variant="outlined" color="success"
              startIcon={applying ? <CircularProgress size={18} /> : <PlayArrowIcon />}
              onClick={handleApplyRules}
              disabled={applying || activeCount === 0}
            >
              Apply Rules
            </Button>
            <Button variant="contained" startIcon={<AddIcon />} onClick={openNewDialog}>
              New Rule
            </Button>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Summary chips */}
        <Box sx={{ display: 'flex', gap: 1, mb: 3, flexWrap: 'wrap' }}>
          <Chip label={`${rules.length} Total Rules`} variant="outlined" />
          <Chip
            label={`${activeCount} Active`}
            sx={{ bgcolor: '#4CAF5020', color: '#4CAF50', border: '1px solid #4CAF5040' }}
          />
          <Chip
            label={`${rules.length - activeCount} Inactive`}
            sx={{ bgcolor: '#9E9E9E20', color: '#9E9E9E', border: '1px solid #9E9E9E40' }}
          />
        </Box>

        {/* Rules list or empty state */}
        {rules.length === 0 ? (
          <EmptyState
            icon={<RuleIcon />}
            title="No Categorization Rules"
            description="Create rules to automatically categorize your transactions based on description, amount, or account."
            actionLabel="Create First Rule"
            onAction={openNewDialog}
          />
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {rules
              .sort((a, b) => b.priority - a.priority)
              .map((rule) => (
                <Card key={rule.id} variant="outlined" sx={{ opacity: rule.is_active ? 1 : 0.6 }}>
                  <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 2, '&:last-child': { pb: 2 } }}>
                    <Tooltip title={rule.is_active ? 'Disable rule' : 'Enable rule'}>
                      <Switch checked={rule.is_active} onChange={() => handleToggleActive(rule)} size="small" />
                    </Tooltip>
                    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                      <Typography variant="subtitle1" fontWeight={600} noWrap>{rule.name}</Typography>
                      <Typography variant="body2" color="text.secondary" noWrap>
                        {describeCondition(rule)}
                      </Typography>
                    </Box>
                    <Chip label={getCategoryName(rule.category_id)} size="small" variant="outlined" sx={{ flexShrink: 0 }} />
                    <Chip
                      label={`P${rule.priority}`} size="small"
                      sx={{ bgcolor: 'primary.main', color: 'primary.contrastText', fontWeight: 600, fontSize: '0.7rem', height: 24, minWidth: 36 }}
                    />
                    <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
                      <Tooltip title="Edit rule">
                        <IconButton size="small" onClick={() => openEditDialog(rule)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete rule">
                        <IconButton size="small" color="error" onClick={() => handleDelete(rule.id)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </CardContent>
                </Card>
              ))}
          </Box>
        )}

        {/* Create/Edit Dialog */}
        <Dialog open={dialogOpen} onClose={closeDialog} maxWidth="sm" fullWidth>
          <DialogTitle>{editingRule ? 'Edit Rule' : 'New Categorization Rule'}</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
              <TextField
                label="Rule Name" value={form.name} fullWidth autoFocus
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="e.g. Netflix Subscription"
              />
              <FormControl fullWidth>
                <InputLabel>Match Field</InputLabel>
                <Select value={form.match_field} label="Match Field"
                  onChange={(e) => setForm({ ...form, match_field: e.target.value })}>
                  <MenuItem value="description">Description</MenuItem>
                  <MenuItem value="amount">Amount</MenuItem>
                  <MenuItem value="account">Account</MenuItem>
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel>Match Type</InputLabel>
                <Select value={form.match_type} label="Match Type"
                  onChange={(e) => setForm({ ...form, match_type: e.target.value })}>
                  <MenuItem value="contains">Contains</MenuItem>
                  <MenuItem value="exact">Exact Match</MenuItem>
                  <MenuItem value="starts_with">Starts With</MenuItem>
                  <MenuItem value="ends_with">Ends With</MenuItem>
                  <MenuItem value="regex">Regex</MenuItem>
                  <MenuItem value="range">Range (between two values)</MenuItem>
                </Select>
              </FormControl>
              <TextField
                label={form.match_type === 'range' ? 'Min Value' : 'Match Value'}
                value={form.match_value} fullWidth
                onChange={(e) => setForm({ ...form, match_value: e.target.value })}
                placeholder={form.match_type === 'range' ? 'e.g. 10.00' : 'e.g. Netflix'}
              />
              {/* Reason: Only show the second value field when match_type is 'range' */}
              {form.match_type === 'range' && (
                <TextField
                  label="Max Value" value={form.match_value_2 || ''} fullWidth
                  onChange={(e) => setForm({ ...form, match_value_2: e.target.value })}
                  placeholder="e.g. 50.00"
                />
              )}
              <FormControl fullWidth>
                <InputLabel>Category</InputLabel>
                <Select value={form.category_id} label="Category"
                  onChange={(e) => setForm({ ...form, category_id: e.target.value })}>
                  {categories.map((cat) => (
                    <MenuItem key={cat.id} value={cat.id}>
                      {cat.group_name} &gt; {cat.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                label="Priority" type="number" value={form.priority} fullWidth
                onChange={(e) => setForm({ ...form, priority: parseInt(e.target.value, 10) || 0 })}
                helperText="Higher priority rules are evaluated first"
              />
              <FormControlLabel
                control={
                  <Switch checked={form.is_active ?? true}
                    onChange={(e) => setForm({ ...form, is_active: e.target.checked })} />
                }
                label="Active"
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={closeDialog}>Cancel</Button>
            <Button variant="contained" onClick={handleSave} disabled={!isFormValid || saving}
              startIcon={saving ? <CircularProgress size={18} /> : undefined}>
              {editingRule ? 'Update' : 'Create'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Test Results Dialog */}
        <RuleTestResultsDialog
          open={testDialogOpen}
          onClose={() => setTestDialogOpen(false)}
          results={testResults}
          total={testTotal}
        />

        {/* Snackbar for success messages */}
        <Snackbar
          open={!!snackbar} autoHideDuration={4000} onClose={() => setSnackbar(null)}
          message={snackbar} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        />
      </Box>
    </MainLayout>
  );
};
